import { FORM_FILE_UPLOAD_ATTACHMENT_PREFIX } from '@constants';
import { FormContext, FormShape, TrackEvent } from '@enums';
import { Enum_Forms_Type } from '@graphql/generated/graphql';
import {
    CustomerInput,
    CustomerInputAttachmentCreate,
    CustomerInputData,
    CustomerInputType,
    ModelFile,
} from '@hello-pimster/pimster-crm-sdk';
import { useAnalytics } from '@lib/analytics/analyticsContext';
import { useApiCrm } from '@lib/pimster-crm/provider/ApiCrmProvider';
import { UseMutateFunction, useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useRouter } from 'next/router';
import { useState } from 'react';

export type AttachmentKey =
    `${typeof FORM_FILE_UPLOAD_ATTACHMENT_PREFIX}${string}`;

export const isAttachmentKey = (value: unknown): value is AttachmentKey => {
    if (typeof value !== 'string') return false;
    if (!value.startsWith(FORM_FILE_UPLOAD_ATTACHMENT_PREFIX)) return false;

    return true;
};

export interface UseFormSubmitProps {
    formName: string;
    formUID: string;
    formType: Enum_Forms_Type;
    formContext?: FormContext;
    formShape: FormShape;
    isProductRegistration: boolean;
    onSuccess?:
        | ((
              data: CustomerInput,
              variables: UseFormSubmitMutateArgs,
              context: unknown
          ) => unknown)
        | undefined;
    onError?:
        | ((
              error: AxiosError,
              variables: UseFormSubmitMutateArgs,
              context: unknown
          ) => unknown)
        | undefined;
}

export interface UseFormSubmitMutateArgs {
    data: Omit<CustomerInputData, '_type' | 'submittedAt'> & {
        [key: string]: unknown;
        [key: AttachmentKey]: ModelFile;
    };
    customerInputType: CustomerInputType;
}

export type UseFormSubmitMutate = UseMutateFunction<
    CustomerInput,
    AxiosError,
    UseFormSubmitMutateArgs,
    unknown
>;

export const useFormSubmit = ({
    formName,
    formUID,
    formType,
    formContext,
    formShape,
    isProductRegistration,
    onSuccess,
    onError,
}: UseFormSubmitProps) => {
    const { client } = useApiCrm();
    const { userAnonymousId, track } = useAnalytics();
    const {
        locale,
        query: { company, product },
    } = useRouter();
    const [error, setError] = useState<AxiosError>();
    const [isSuccessful, setIsSuccessful] = useState<boolean>(false);

    const { mutate, isPending } = useMutation<
        CustomerInput,
        AxiosError,
        UseFormSubmitMutateArgs,
        unknown
    >({
        mutationFn: async ({
            data,
            customerInputType,
        }: UseFormSubmitMutateArgs) => {
            if (!locale || !userAnonymousId) {
                throw new Error('Invalid data');
            }

            track(TrackEvent.FormSubmitted, {
                name: formName,
                form_uid: formUID,
                type: formType,
                context: formContext,
                shape: formShape,
            });

            const attachments: CustomerInputAttachmentCreate[] = Object.entries(
                data
            ).reduce<CustomerInputAttachmentCreate[]>((acc, entries) => {
                if (isAttachmentKey(entries[0]) && data[entries[0]]) {
                    const name = entries[0].slice(
                        FORM_FILE_UPLOAD_ATTACHMENT_PREFIX.length
                    );

                    acc.push({ name, file: data[entries[0]] });
                    delete data[entries[0]];
                }
                return acc;
            }, []);

            const res = await client.customerInputControllerCreate({
                customerInputCreate: {
                    attachments,
                    locale,
                    isProductRegistration,
                    cookieId: userAnonymousId,
                    type: customerInputType,
                    form: {
                        UID: formUID,
                        name: formName,
                    },
                    product: {
                        cmsUID: `${company}::${product}`,
                    },
                    // @ts-ignore
                    data: {
                        ...data,
                        _type: customerInputType,
                        submittedAt: new Date().toISOString(),
                    },
                },
            });

            return res.data;
        },
        onSuccess: (...params) => {
            setIsSuccessful(true);

            return onSuccess?.(...params);
        },
        onError: (error, variables, context) => {
            track(TrackEvent.FormError, {
                name: formName,
                form_uid: formUID,
                type: formType,
                context: formContext,
                shape: formShape,
                error: error.message,
            });
            setError(error);
            onError?.(error, variables, context);
            console.warn(
                "[useFormSubmit]: Unable to send form's data: ",
                error
            );
        },
    });

    return {
        error,
        isSuccessful,
        isSubmitting: isPending,
        onSubmit: mutate,
    };
};
